// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableBody, DataTableHead, VisuallyHidden } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import HomeAccordion from "@steiner/App/Components/Home/HomeAccordion";
import Notify from "@steiner/App/Components/Notify";
import acceptBidHandler from "@steiner/App/Handlers/OpenBids/AcceptBid";
import loadOpenBids, { defaultParameters } from "@steiner/App/Handlers/XNG/LoadOpenBids";
import { CustomAppState, OpenBidOrderModel } from "@steiner/App/Store/Reducers";
import OpenBidsItem from "@steiner/App/Widgets/OpenBids/OpenBidsItem";
import DataTable, { DataTableProps } from "@steiner/MobiusOverrides/DataTable";
import DataTableHeader from "@steiner/MobiusOverrides/DataTable/DataTableHeader";
import { DatePickerState } from "@steiner/MobiusOverrides/DatePicker";
import GridContainer, { GridContainerProps } from "@steiner/MobiusOverrides/GridContainer/GridContainer";
import Pagination, { PaginationPresentationProps } from "@steiner/MobiusOverrides/Pagination";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import React, { useEffect, useRef, useState } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { useReactToPrint } from "react-to-print";

const enum fields {
    showBidStatus = "showBidStatus",
    showReleaseNumber = "showReleaseNumber",
    showShipDate = "showShipDate",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.showBidStatus]: boolean;
        [fields.showReleaseNumber]: boolean;
        [fields.showShipDate]: boolean;
    };
}

const labels = {
    date: translate("Date"),
    bidNumber: translate("Bid #"),
    poName: translate("PO / Job Name"),
    shipDate: translate("Ship Date"),
    releaseNumber: translate("Release #"),
    expirationDate: translate("Expiration Date"),
    amount: translate("Amount"),
    bidStatus: translate("Bid Status"),
};

export interface OpenBidsStyles {
    dataTable?: DataTableProps;
    dataTableCell?: DataTableCellProps;
    notFoundContainer?: InjectableCss;
    notFoundMessage?: TypographyProps;
    customerInfoContainer?: InjectableCss;
    customerInfo?: TypographyPresentationProps;
    printBidsLink?: LinkPresentationProps;
}

export const openBidsStyles: OpenBidsStyles = {};

const styles = openBidsStyles;

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const billToState = getBillToState(state, getSession(state).billToId).value;
    return {
        orders: state.xOpenBids.orders,
        isLoading: state.xOpenBids.isLoading,
        companyName: billToState?.companyName,
        address: billToState?.address1,
        phone: billToState?.phone,
        openBidsPageUrl: getPageLinkByPageType(state, "OpenBidsPage")?.url,
        pagination: state.xOpenBids.pagination,
        isOpenBidsPage: getCurrentPage(state).type === "OpenBidsPage",
    };
};

const mapDispatchToProps = {
    loadOpenBids,
    acceptBidHandler,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export const OpenBidsTable: FC<Props> = ({
    orders,
    pagination,
    isLoading,
    companyName,
    address,
    phone,
    openBidsPageUrl,
    loadOpenBids,
    isOpenBidsPage,
    fields: { showShipDate, showReleaseNumber, showBidStatus },
}) => {
    const openBidsContentRef = useRef<HTMLDivElement>(null);
    const print = useReactToPrint({
        contentRef: openBidsContentRef,
        pageStyle: "@media print { @page {}}",
    });

    useEffect(() => {
        loadOpenBids({
            ...defaultParameters,
            pageSize: isOpenBidsPage ? pagination?.defaultPageSize || 16 : 8,
        });
    }, []);

    if (!orders || orders.length === 0) {
        return null;
    }

    const customerInfo = (
        <StyledWrapper {...styles.customerInfoContainer}>
            <Typography {...styles.customerInfo}>
                {translate("Customer: ")}
                <Typography weight={fontConfig.weight.semiBold}>{companyName}</Typography>
            </Typography>
            <Typography {...styles.customerInfo}>
                {translate("Address: ")}
                <Typography weight={fontConfig.weight.semiBold}>{address}</Typography>
            </Typography>
            <Typography {...styles.customerInfo}>
                {translate("Phone: ")}
                <Typography weight={fontConfig.weight.semiBold}>{phone}</Typography>
            </Typography>
            <Link
                onClick={() => {
                    print();
                }}
                {...styles.printBidsLink}
            >
                {translate("Print Bids")}
            </Link>
        </StyledWrapper>
    );

    return (
        <StyledWrapper ref={openBidsContentRef}>
            <HomeAccordion
                title={translate("Open Bids")}
                pagination={pagination}
                itemsCount={orders?.length || 0}
                viewAll={openBidsPageUrl}
                showViewAll={!isOpenBidsPage}
                isLoading={isLoading}
                showPagination={true}
            >
                {customerInfo}
                {!orders || orders.length === 0 ? (
                    <>
                        {!isLoading && (
                            <StyledWrapper {...styles.notFoundContainer}>
                                <Notify {...styles.notFoundMessage}>{translate("No open bids found.")}</Notify>
                            </StyledWrapper>
                        )}
                    </>
                ) : (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            <DataTableHeader>{labels.date}</DataTableHeader>
                            <DataTableHeader>{labels.bidNumber}</DataTableHeader>
                            <DataTableHeader>{labels.poName}</DataTableHeader>
                            {showShipDate && <DataTableHeader>{labels.shipDate}</DataTableHeader>}
                            {showReleaseNumber && <DataTableHeader>{labels.releaseNumber}</DataTableHeader>}
                            <DataTableHeader>{labels.expirationDate}</DataTableHeader>
                            <DataTableHeader>{labels.amount}</DataTableHeader>
                            {showBidStatus && (
                                <DataTableHeader>
                                    <VisuallyHidden>{labels.bidStatus}</VisuallyHidden>
                                </DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody>
                            {orders.map(order => {
                                return (
                                    <OpenBidsItem
                                        key={order.orderId}
                                        labels={labels}
                                        openBid={order}
                                        showColumns={{ showBidStatus, showReleaseNumber, showShipDate }}
                                    />
                                );
                            })}
                        </DataTableBody>
                    </DataTable>
                )}
            </HomeAccordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(OpenBidsTable)),
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.showShipDate,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showReleaseNumber,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showBidStatus,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
        allowedContexts: ["HomePage", "MyAccountPage"],
    },
};

export default widgetModule;
